import { createHttpCore, type HttpCore } from './http';
import { createInMemoryQueueStore, type OfflineQueueStore } from './offlineQueue';
import type { ApiClientConfig } from './types';
import { createAuthModule } from './modules/auth';
import { createMeModule } from './modules/me';
import { createPortalModule } from './modules/portal';
import { createCoursesModule } from './modules/courses';
import { createReadingModule } from './modules/reading';
import { createLabsModule } from './modules/labs';
import { createEnvironmentsModule } from './modules/environments';
import { createGradesModule } from './modules/grades';
import { createHitlModule } from './modules/hitl';
import { createNotificationsModule } from './modules/notifications';
import { createTutorModule } from './modules/tutor';
import { createPaymentsModule } from './modules/payments';

export interface ApiClient {
  auth: ReturnType<typeof createAuthModule>;
  me: ReturnType<typeof createMeModule>;
  portal: ReturnType<typeof createPortalModule>;
  courses: ReturnType<typeof createCoursesModule>;
  reading: ReturnType<typeof createReadingModule>;
  labs: ReturnType<typeof createLabsModule>;
  environments: ReturnType<typeof createEnvironmentsModule>;
  grades: ReturnType<typeof createGradesModule>;
  hitl: ReturnType<typeof createHitlModule>;
  notifications: ReturnType<typeof createNotificationsModule>;
  tutor: ReturnType<typeof createTutorModule>;
  payments: ReturnType<typeof createPaymentsModule>;
  /** Escape hatch for a caller that needs a raw typed request against an endpoint no module covers yet. */
  request: HttpCore['request'];
  flushOfflineQueue: HttpCore['flushOfflineQueue'];
  queueSize: HttpCore['queueSize'];
}

export function createApiClient(config: ApiClientConfig, offlineQueue?: OfflineQueueStore): ApiClient {
  const core = createHttpCore(config, offlineQueue ?? createInMemoryQueueStore());
  return {
    auth: createAuthModule(core),
    me: createMeModule(core),
    portal: createPortalModule(core),
    courses: createCoursesModule(core),
    reading: createReadingModule(core, config.fetch),
    labs: createLabsModule(core),
    environments: createEnvironmentsModule(core),
    grades: createGradesModule(core),
    hitl: createHitlModule(core),
    notifications: createNotificationsModule(core),
    tutor: createTutorModule(core),
    payments: createPaymentsModule(core),
    request: core.request,
    flushOfflineQueue: core.flushOfflineQueue,
    queueSize: core.queueSize,
  };
}
